// ФАЙЛ: frontend/src/services/suppliers.js
// ОПИСАНИЕ: Унифицированный API сервис для работы с любыми поставщиками
// Заменяет RS24-специфичный сервис на универсальный

import axios from '../utils/axios';

const SUPPLIERS_API = {
  // ================================================================
  // БАЗОВЫЕ ОПЕРАЦИИ
  // ================================================================

  /**
   * Получение всех поставщиков текущей компании
   */
  getSuppliers: async (params = {}) => {
    try {
      return await axios.get('/api/suppliers', { 
        params,
        // Список используется в CRUD/фильтрах — возвращаем максимально актуальные данные.
        cacheTtlMs: 0
      });
    } catch (error) {
      // ✅ Fallback: в некоторых деплоях /api/suppliers может отсутствовать/не быть примонтирован.
      // Для отображения товаров достаточно словарного списка поставщиков.
      const status = error?.response?.status;
      if (status === 404) {
        return await axios.get('/api/dictionaries/suppliers', { 
          params,
          cacheTtlMs: 5 * 60 * 1000 // Справочник - долгий кэш
        });
      }
      throw error;
    }
  },

  // ================================================================
  // УПРАВЛЕНИЕ УЧЕТНЫМИ ДАННЫМИ
  // ================================================================

  /**
   * Сохранение учетных данных поставщика
   * Если supplierId не указан — бэкенд создаст RS24 поставщика автоматически.
   */
  saveCredentials: (supplierId, data) =>
    axios.post('/api/suppliers/credentials', { ...(supplierId ? { supplierId } : {}), ...data }),

  /**
   * Получение учетных данных поставщика
   */
  getCredentials: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/credentials`),

  /**
   * Defaults заказов (location/skl/ILN) — SSOT App → ORDERS sync
   */
  saveOrderDefaults: (supplierId, data) =>
    axios.patch(`/api/suppliers/${supplierId}/order-defaults`, data),

  // ================================================================
  // СИНХРОНИЗАЦИЯ И ЗАГРУЗКА ДАННЫХ
  // ================================================================

  /**
   * Получение складов поставщика
   */
  getWarehouses: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/warehouses`),

  /**
   * Тестирование подключения поставщика
   */
  testSupplier: (supplierId, data = {}) =>
    axios.post(`/api/suppliers/${supplierId}/test`, data),

  /**
   * Синхронизация складов
   */
  syncWarehouses: (supplierId) =>
    axios.post(`/api/suppliers/${supplierId}/sync/warehouses`),

  /**
   * Синхронизация товаров
   */
  syncProducts: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/products`, data),

  /**
   * Синхронизация позиций (алиас, единый нейминг)
   */
  syncPositions: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/positions`, data),

  /**
   * Синхронизация цен
   */
  syncPrices: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/prices`, data),

  /**
   * Синхронизация цен по брендам
   */
  syncPricesByBrands: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/prices/by-brands`, data),

  /**
   * Синхронизация остатков
   */
  syncStocks: (supplierId, warehouseId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/stocks/${warehouseId}`, data),

  /**
   * Синхронизация остатков (без привязки к складу)
   */
  syncStocksAll: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/stocks`, data),

  /**
   * Синхронизация характеристик товаров
   */
  syncSpecs: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/specs`, data),

  /**
   * Постановка задачи скачивания характеристик по external_code/external_id
   */
  downloadSupplierSpecs: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/supplier-specs/download`, data),

  /**
   * Зеркалирование медиа (фото/сертификаты/брошюры) → FILES/S3
   * Body: { productCodes?: string[], types?: string[] }
   */
  mirrorSupplierMedia: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/media/mirror`, data),

  /**
   * Синхронизация характеристик по выбранным нашим брендам
   */
  syncSpecsByBrands: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/sync/specs/by-brands`, data),

  // ================================================================
  // УПРАВЛЕНИЕ МАППИНГОМ БРЕНДОВ
  // ================================================================

  /**
   * Получение маппинга брендов
   */
  getBrandMappings: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/brand-mappings`),

  /**
   * Сохранение маппинга бренда
   */
  saveBrandMapping: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/brand-mappings`, data),

  /**
   * Удаление маппинга бренда
   */
  deleteBrandMapping: (supplierId, supplierBrand) =>
    axios.delete(`/api/suppliers/${supplierId}/brand-mappings/${supplierBrand}`),

  /**
   * Внешние бренды поставщика
   */
  getExternalBrands: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/external-brands`),

  // ================================================================
  // УПРАВЛЕНИЕ ТОВАРАМИ
  // ================================================================

  /**
   * Получение списка товаров с фильтрацией
   */
  getProducts: (supplierId, params = {}, { signal } = {}) => {
    // Список поставщика содержит складской статус, наличие и цены, поэтому он должен
    // обновляться сразу при смене склада/сортировки/фильтра. Долгий GET-кэш здесь
    // создаёт рассинхрон: после первого захода видны старые поля, а сортировка уже
    // приносит новый ответ.
    const hasLiveParams = Boolean(
      params.warehouseId ||
      params.warehouse_id ||
      params.stock_status ||
      params.low_stock ||
      params.sortBy ||
      params.sortOrder ||
      params.warehouseCategory ||
      params.brands ||
      params.series ||
      params.search
    );
    const cacheTtl = hasLiveParams ? 0 : 30 * 1000;
    return axios.get(`/api/suppliers/${supplierId}/products`, { 
      params, 
      cacheTtlMs: cacheTtl,
      signal,
      noDedup: Boolean(signal)
    });
  },

  /**
   * Фоновая догрузка тяжёлых полей для товаров поставщика (остатки/цены/характеристики)
   */
  getProductsBulkFields: (supplierId, data = {}, { signal } = {}) =>
    axios.post(`/api/suppliers/${supplierId}/products/bulk-fields`, data, { signal }),

  /**
   * Удаление товара поставщика по externalId (доступно только для помеченных на удаление)
   */
  deleteProduct: (supplierId, externalId) =>
    axios.delete(`/api/suppliers/${supplierId}/products/to-remove`, { data: { external_ids: [externalId] } }),

  /**
   * Удаление любых товаров поставщика (включая все связанные данные)
   */
  deleteAnyProducts: (supplierId, externalIds) =>
    axios.delete(`/api/suppliers/${supplierId}/products/any`, { data: { external_ids: externalIds } }),

  /**
   * Удаление характеристик товаров поставщика
   */
  deleteProductSpecs: (supplierId, externalIds) =>
    axios.delete(`/api/suppliers/${supplierId}/products/specs`, { data: { external_ids: externalIds } }),

  /**
   * Получение всех id товаров по текущим фильтрам (для Select All)
   */
  getProductIds: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/product-ids`, {
      params,
      cacheTtlMs: 60 * 1000,
      timeout: 120000
    }),

  /**
   * Быстрая статистика по товарам поставщика (всего/новые/к удалению)
   */
  getProductsStats: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/products/stats`, { cacheTtlMs: 30 * 1000 }),

  getFeedCoverage: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/feed-coverage`, { cacheTtlMs: 30 * 1000 }),

  /**
   * Покрытие полной выгрузки (алиас catalog-diff).
   */
  getCatalogDiff: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/feed-coverage`, { cacheTtlMs: 30 * 1000 }),

  /**
   * Сопоставление товаров
   */
  matchProducts: (supplierId, data = {}) =>
    axios.post(
      `/api/product-import/match-products`,
      { ...(data || {}), supplier_id: supplierId },
      { timeout: 300000 }
    ),

  getMatchingRules: (params = {}) =>
    axios.get('/api/product-import/matching-rules', { params }),

  saveMatchingRule: (data) =>
    axios.post('/api/product-import/matching-rules', data),

  updateMatchingRule: (id, data) =>
    axios.put(`/api/product-import/matching-rules/${id}`, data),

  deleteMatchingRule: (id) =>
    axios.delete(`/api/product-import/matching-rules/${id}`),

  /**
   * Импорт или сопоставление товаров
   */
  importOrMatch: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/import-or-match`, data),

  // ================================================================
  // ЭКСПОРТ ДАННЫХ
  // ================================================================

  /**
   * Экспорт товаров в CSV
   */
  exportProducts: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/export/products`, data),

  /**
   * Экспорт цен в CSV
   */
  exportPrices: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/export/prices`, data),

  /**
   * Экспорт остатков в CSV
   */
  exportStocks: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/export/stocks`, data),

  // ================================================================
  // ПОЛУЧЕНИЕ АКТУАЛЬНЫХ ДАННЫХ
  // ================================================================

  /**
   * Получение логов синхронизации
   */
  getSyncLogs: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/sync-logs`, { params }),

  /**
   * Получение последних цен
   */
  getLatestPrices: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/latest-prices`, { params }),

  /**
   * Получение последних остатков
   */
  getLatestStocks: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/latest-stocks`, { params }),

  /**
   * Получение характеристик товара
   */
  getProductSpecs: (supplierId, code) =>
    axios.get(`/api/suppliers/${supplierId}/specs/${code}`),

  /**
   * Подробные цены по всем складам поставщика для товара
   */
  getProductPrices: (supplierId, externalId) =>
    axios.get(`/api/suppliers/${supplierId}/products/${encodeURIComponent(String(externalId))}/prices`, { cacheTtlMs: 30 * 1000 }),

  /**
   * Подробные остатки по всем складам поставщика для товара
   */
  getProductStocks: (supplierId, externalId) =>
    axios.get(`/api/suppliers/${supplierId}/products/${encodeURIComponent(String(externalId))}/stocks`, { cacheTtlMs: 0 }), // ❌ НЕ КЭШИРУЕМ: Наличие

  /**
   * Получение живых характеристик (прямо из API поставщика)
   */
  getProductSpecsLive: (supplierId, code) =>
    axios.get(`/api/suppliers/${supplierId}/specs-live/${code}`),

  // ================================================================
  // СТАТИСТИКА И МОНИТОРИНГ
  // ================================================================

  /**
   * Получение статистики поставщика
   */
  getStats: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/stats`),

  /**
   * Очистка кэша
   */
  clearCache: (supplierId) =>
    axios.post(`/api/suppliers/${supplierId}/clear-cache`),

  // ================================================================
  // ПРАВИЛА ИМПОРТА ПОСТАВЩИКА
  // ================================================================
  getImportRules: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/import-rules`, { cacheTtlMs: 0, silent404: true }),
  saveImportRules: (supplierId, data) =>
    axios.put(`/api/suppliers/${supplierId}/import-rules`, data),
  deleteImportRules: (supplierId) =>
    axios.delete(`/api/suppliers/${supplierId}/import-rules`),

  /**
   * Получение статуса синхронизации
   */
  getSyncStatus: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/sync-status`),

  /**
   * Остановка синхронизации
   */
  stopSync: (supplierId) =>
    axios.post(`/api/suppliers/${supplierId}/stop-sync`),

  /**
   * Получение ошибок синхронизации
   */
  getSyncErrors: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/sync-errors`, { params }),

  /**
   * Повтор синхронизации
   */
  retrySync: (supplierId, syncId) =>
    axios.post(`/api/suppliers/${supplierId}/retry-sync/${syncId}`),

  // ================================================================
  // УПРАВЛЕНИЕ ФАЙЛАМИ ИМПОРТА
  // ================================================================

  /**
   * Получение файлов импорта
   */
  getImportFiles: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/import-files`, { params }),

  /**
   * Загрузка файла импорта
   */
  uploadImportFile: (supplierId, formData) => {
    return axios.post(`/api/suppliers/${supplierId}/upload-import-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Обработка файла импорта
   */
  processImportFile: (supplierId, fileId) =>
    axios.post(`/api/suppliers/${supplierId}/process-import-file/${fileId}`),

  /**
   * Удаление файла импорта
   */
  deleteImportFile: (supplierId, fileId) =>
    axios.delete(`/api/suppliers/${supplierId}/import-files/${fileId}`),

  // ================================================================
  // СОПОСТАВЛЕНИЕ ТОВАРОВ
  // ================================================================

  /**
   * Получение сопоставлений товаров
   */
  getProductMatches: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/product-matches`, { params }),

  /**
   * Создание сопоставления товара
   */
  createProductMatch: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/product-matches`, data),

  /**
   * Удаление сопоставления товара
   */
  deleteProductMatch: (supplierId, matchId) =>
    axios.delete(`/api/suppliers/${supplierId}/product-matches/${matchId}`),

  /**
   * Массовое сопоставление товаров
   */
  bulkMatchProducts: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/bulk-match-products`, data),

  /**
   * Получение предложений для сопоставления
   */
  getMatchSuggestions: (supplierId, productId) =>
    axios.get(`/api/suppliers/${supplierId}/match-suggestions/${productId}`),

  // ================================================================
  // ИСТОРИЯ ИЗМЕНЕНИЙ
  // ================================================================

  /**
   * Получение истории изменений
   */
  getChangeHistory: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/change-history`, { params }),

  /**
   * Откат изменений
   */
  rollbackChanges: (supplierId, changeId) =>
    axios.post(`/api/suppliers/${supplierId}/rollback-changes/${changeId}`),

  // ================================================================
  // НАСТРОЙКИ ИНТЕГРАЦИИ
  // ================================================================

  /**
   * Получение настроек интеграции
   */
  getIntegrationSettings: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/integration-settings`),

  /**
   * Сохранение настроек интеграции
   */
  saveIntegrationSettings: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/integration-settings`, data),

  // ================================================================
  // ОТЧЕТЫ И МЕТРИКИ
  // ================================================================

  /**
   * Получение метрик производительности
   */
  getPerformanceMetrics: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/performance-metrics`, { params }),

  /**
   * Получение отчетов
   */
  getReports: (supplierId, params = {}) =>
    axios.get(`/api/suppliers/${supplierId}/reports`, { params }),

  /**
   * Генерация отчета
   */
  generateReport: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/generate-report`, data),

  /**
   * Скачивание отчета
   */
  downloadReport: (supplierId, reportId) =>
    axios.get(`/api/suppliers/${supplierId}/download-report/${reportId}`, {
      responseType: 'blob',
    }),

  // ================================================================
  // РАСПИСАНИЯ
  // ================================================================

  /**
   * Получение расписаний
   */
  getSchedules: (supplierId) =>
    axios.get(`/api/suppliers/${supplierId}/schedules`),

  /**
   * Создание расписания
   */
  createSchedule: (supplierId, data) =>
    axios.post(`/api/suppliers/${supplierId}/schedules`, data),

  /**
   * Обновление расписания
   */
  updateSchedule: (supplierId, id, data) =>
    axios.put(`/api/suppliers/${supplierId}/schedules/${id}`, data),

  /**
   * Удаление расписания
   */
  deleteSchedule: (supplierId, id) =>
    axios.delete(`/api/suppliers/${supplierId}/schedules/${id}`),

  // ================================================================
  // УПРАВЛЕНИЕ ПОСТАВЩИКАМИ
  // ================================================================

  /**
   * Удаление поставщика
   */
  deleteSupplier: (supplierId) =>
    axios.delete(`/api/suppliers/${supplierId}`),
};

export default SUPPLIERS_API;
